import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, TrendingUp, TrendingDown, Lightbulb, AlertCircle,
  Grid3x3, List, Search, Filter, CheckCircle, ArrowRight,
  Package, Sparkles, Plus, Trash2, X, Eye, Download, Edit2
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import apiClient from '../../utils/apiClient';
import { toast } from 'sonner';

export default function Skills() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  
  // Helper to get translated category label
  const getCategoryLabel = (category) => {
    if (!category || category === 'all') return t('skills.filterAll');
    // Try to get from categories namespace, fallback to original
    const translated = t(`categories.${category}`, { defaultValue: '' });
    return translated || category;
  };
  
  // State
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('installed');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  
  // Data
  const [installedSkills, setInstalledSkills] = useState([]);
  const [librarySkills, setLibrarySkills] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterInstalled, setFilterInstalled] = useState('all'); // all, excellent, good, fair, poor
  const [filterLibrary, setFilterLibrary] = useState('all'); // all, marketplace, custom
  
  // Install dialog
  const [installDialogOpen, setInstallDialogOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [installConfig, setInstallConfig] = useState({
    approvalMode: 'assisted',
    allowTrainingData: true
  });
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null,
    confirmText: 'Confirm',
    variant: 'destructive'
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [installedRes, ownedRes, createdRes, suggestionsRes] = await Promise.all([
        apiClient.get('/skills/my-skills?filter=installed'),
        apiClient.get('/skills/my-skills?filter=owned'),
        apiClient.get('/skills/my-skills?filter=created'),
        apiClient.get('/skills/learning/suggestions?status=pending')
      ]);

      setInstalledSkills(installedRes.data);
      
      // Library shows ALL owned and created skills (including installed ones)
      const installedIds = new Set(installedRes.data.map(s => s.skillPackageId));
      const createdIds = new Set(createdRes.data.map(s => s.skillPackageId));
      
      // Mark owned (marketplace) skills as NOT custom and check if installed
      const ownedSkills = ownedRes.data
        .filter(s => !createdIds.has(s.skillPackageId)) // Avoid duplicates with created skills
        .map(s => ({
          ...s,
          isCustom: false,
          isInstalled: installedIds.has(s.skillPackageId)
        }));
      
      // Mark created skills as custom
      const createdSkills = createdRes.data.map(s => ({
        ...s,
        isCustom: true,
        isCreatedByMe: true,
        isInstalled: installedIds.has(s.skillPackageId)
      }));
      
      setLibrarySkills([...ownedSkills, ...createdSkills]);
      setSuggestions(suggestionsRes.data);
      
      // Fetch analytics separately and handle errors gracefully
      try {
        const analyticsRes = await apiClient.get('/skills/analytics/overview');
        setAnalyticsData(analyticsRes.data);
      } catch (analyticsError) {
        console.warn('Analytics not available:', analyticsError);
        setAnalyticsData({
          overview: {
            totalSkills: installedRes.data.length,
            averageRating: 0,
            trendingUp: 0,
            needsAttention: 0
          },
          skills: []
        });
      }
    } catch (error) {
      console.error('Failed to fetch skills data:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Get analytics for a specific skill
  const getSkillAnalytics = (skillPackageId) => {
    if (!analyticsData) return null;
    return analyticsData.skills.find(s => s.skillPackageId === skillPackageId);
  };

  // Get suggestions for a specific skill
  const getSkillSuggestions = (skillPackageId) => {
    return suggestions.filter(s => s.skillPackageId === skillPackageId);
  };

  // Filter installed skills
  const getFilteredInstalledSkills = () => {
    let filtered = installedSkills;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(skill =>
        skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        skill.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Source filter (marketplace vs custom)
    if (filterInstalled === 'marketplace') {
      filtered = filtered.filter(skill => !skill.isCustom);
    } else if (filterInstalled === 'custom') {
      filtered = filtered.filter(skill => skill.isCustom);
    }

    return filtered;
  };

  // Filter library skills
  const getFilteredLibrarySkills = () => {
    let filtered = librarySkills;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(skill =>
        skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        skill.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Source filter
    if (filterLibrary === 'marketplace') {
      filtered = filtered.filter(skill => !skill.isCustom);
    } else if (filterLibrary === 'custom') {
      filtered = filtered.filter(skill => skill.isCustom);
    }

    return filtered;
  };

  const getHealthBadge = (health) => {
    const badges = {
      excellent: { color: 'bg-green-100 text-green-700', label: t('analytics.excellent') },
      good: { color: 'bg-blue-100 text-blue-700', label: t('analytics.good') },
      fair: { color: 'bg-yellow-100 text-yellow-700', label: t('analytics.fair') },
      poor: { color: 'bg-red-100 text-red-700', label: t('analytics.poor') }
    };
    return badges[health] || badges.good;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (trend === 'declining') return <TrendingDown className="w-4 h-4 text-red-600" />;
    return null;
  };

  // Calculate quick stats
  const quickStats = {
    totalInstalled: installedSkills.length,
    averageRating: analyticsData?.overview?.averageRating || 0,
    pendingSuggestions: suggestions.length,
    needsAttention: analyticsData?.overview?.needsAttention || 0
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className={isRTL ? 'text-right' : ''}>
        <h1 className="font-heading text-3xl font-bold text-gray-900">
          {t('skills.title')}
        </h1>
        <p className="text-gray-600 mt-2">
          {t('skills.subtitle')}
        </p>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={isRTL ? 'text-right' : ''}>
              <p className="text-sm text-gray-600">{t('skills.installed')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{quickStats.totalInstalled}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-xl">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={isRTL ? 'text-right' : ''}>
              <p className="text-sm text-gray-600">{t('analytics.averageRating')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{quickStats.averageRating}%</p>
            </div>
            <div className="p-3 bg-green-100 rounded-xl">
              <BarChart3 className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={isRTL ? 'text-right' : ''}>
              <p className="text-sm text-gray-600">{t('skills.suggestions')}</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">{quickStats.pendingSuggestions}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-xl">
              <Lightbulb className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 hover:shadow-md transition-shadow">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={isRTL ? 'text-right' : ''}>
              <p className="text-sm text-gray-600">{t('skills.alerts')}</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">{quickStats.needsAttention}</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-xl">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content with Tabs */}
      <Card className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="installed">{t('skills.installedTab')}</TabsTrigger>
            <TabsTrigger value="library">{t('skills.libraryTab')}</TabsTrigger>
          </TabsList>

          {/* Installed Tab */}
          <TabsContent value="installed" className="mt-6 space-y-6">
            {/* Controls */}
            <div className={`flex items-center justify-between gap-4 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="relative flex-1 min-w-[200px]">
                  <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400`} />
                  <input
                    type="text"
                    placeholder={t('common.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary`}
                  />
                </div>
                
                <select
                  value={filterInstalled}
                  onChange={(e) => setFilterInstalled(e.target.value)}
                  className={`px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${isRTL ? 'text-right' : ''}`}
                >
                  <option value="all">{t('skills.filterAll')}</option>
                  <option value="marketplace">{t('skills.filterMarketplace')}</option>
                  <option value="custom">{t('skills.filterCustom')}</option>
                </select>
              </div>

              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid3x3 className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Skills Grid */}
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
              {getFilteredInstalledSkills().map((skill) => {
                const analytics = getSkillAnalytics(skill.skillPackageId);
                const skillSuggestions = getSkillSuggestions(skill.skillPackageId);
                const healthBadge = analytics ? getHealthBadge(analytics.health) : null;

                return (
                  <Card 
                    key={skill.skillPackageId} 
                    className="p-5 hover:shadow-xl hover:border-primary/50 transition-all duration-200 cursor-pointer border-2 hover:scale-[1.02]"
                    onClick={() => navigate(`/personal/skills/${skill.skillPackageId}`)}
                  >
                    <div className="space-y-4">
                      {/* Skill Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{skill.name}</h3>
                          <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                            {skill.description}
                          </p>
                        </div>
                        {skill.icon && (
                          <div className="ml-3">
                            {skill.icon}
                          </div>
                        )}
                      </div>

                      {/* Analytics Section */}
                      {analytics && (
                        <div className="space-y-2 cursor-pointer" onClick={() => navigate(`/personal/skills/${skill.skillPackageId}`)}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-base font-semibold text-gray-900">
                                {analytics.ratings.positivePercentage.toFixed(1)}%
                              </span>
                              <span className="text-sm text-gray-600">
                                ({analytics.totalFeedback} {t('skills.ratings')})
                              </span>
                            </div>
                            {healthBadge && (
                              <Badge className={healthBadge.color}>
                                {healthBadge.label}
                              </Badge>
                            )}
                          </div>

                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            {getTrendIcon(analytics.trend)}
                            <span>{analytics.trend === 'stable' ? t('skills.stable') : analytics.trend}</span>
                          </div>
                        </div>
                      )}

                      {/* No analytics yet */}
                      {!analytics && (
                        <div className="text-sm text-gray-500 italic cursor-pointer" onClick={() => navigate(`/personal/skills/${skill.skillPackageId}`)}>
                          {t('skills.noAnalytics')}
                        </div>
                      )}

                      {/* Suggestions Badge */}
                      {skillSuggestions.length > 0 && (
                        <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg cursor-pointer" onClick={() => navigate(`/personal/skills/${skill.skillPackageId}`)}>
                          <Lightbulb className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-purple-700 font-medium">
                            {skillSuggestions.length} {t('skills.suggestion')}
                          </span>
                        </div>
                      )}

                      {/* All good badge */}
                      {!skillSuggestions.length && analytics?.health === 'excellent' && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg cursor-pointer" onClick={() => navigate(`/personal/skills/${skill.skillPackageId}`)}>
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-green-700 font-medium">
                            {t('skills.allGood')}
                          </span>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => navigate(`/personal/skills/${skill.skillPackageId}`)}
                          title={t('skills.viewDetails')}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {skill.isCustom && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => navigate(`/personal/twin/custom-skills/edit/${skill.skillPackageId}`)}
                            title={t('common.edit')}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setConfirmDialog({
                              open: true,
                              title: t('skills.confirmUninstall'),
                              description: `${t('skills.confirmUninstallDesc')} "${skill.name}"?`,
                              confirmText: t('skills.uninstall'),
                              variant: 'destructive',
                              onConfirm: async () => {
                                try {
                                  await apiClient.delete(`/skills/uninstall/${skill.skillPackageId}`);
                                  toast.success(t('skills.uninstalled'));
                                  await fetchAllData();
                                } catch (error) {
                                  toast.error(t('errors.operationFailed'));
                                }
                              }
                            });
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title={t('skills.uninstall')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Empty State */}
            {getFilteredInstalledSkills().length === 0 && (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">{t('skills.noInstalledSkills')}</p>
                <Button className="mt-4" onClick={() => setActiveTab('library')}>
                  {t('skills.browseLibrary')}
                </Button>
              </div>
            )}

            {/* Pending Actions Section */}
            {suggestions.length > 0 && (
              <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Lightbulb className="w-6 h-6 text-purple-600" />
                    <h3 className="font-semibold text-gray-900">{t('skills.pendingActions')} ({suggestions.length})</h3>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {suggestions.slice(0, 3).map((suggestion) => (
                    <div
                      key={suggestion.suggestionId}
                      className="flex items-center justify-between p-3 bg-white rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/personal/skills/${suggestion.skillPackageId}`)}
                    >
                      <div className="flex items-center gap-3">
                        <AlertCircle className="w-4 h-4 text-orange-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{suggestion.title}</p>
                          <p className="text-xs text-gray-600">{suggestion.skillName}</p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Library Tab */}
          <TabsContent value="library" className="mt-6 space-y-6">
            {/* Controls */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2 flex-1">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('skills.searchLibrary')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                
                <select
                  value={filterLibrary}
                  onChange={(e) => setFilterLibrary(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">{t('skills.allSkills')}</option>
                  <option value="marketplace">{t('skills.marketplaceOnly')}</option>
                  <option value="custom">{t('skills.customOnly')}</option>
                </select>
              </div>

              <Button onClick={() => navigate('/personal/twin/custom-skills/new')}>
                <Plus className="w-4 h-4 mr-2" />
                {t('skills.createCustom')}
              </Button>
            </div>

            {/* Library Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getFilteredLibrarySkills().map((skill) => (
                <Card 
                  key={skill.skillPackageId} 
                  className="p-5 hover:shadow-xl hover:border-primary/50 transition-all duration-200 border-2 hover:scale-[1.02]"
                >
                  <div className="space-y-4">
                    {/* Skill Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{skill.name}</h3>
                          {skill.isCustom && (
                            <Badge variant="outline" className="text-xs">
                              <Sparkles className="w-3 h-3 mr-1" />
                              {t('skills.custom')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {skill.description}
                        </p>
                      </div>
                    </div>

                    {/* Rating (for marketplace skills) */}
                    {!skill.isCustom && skill.rating && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span>⭐ {skill.rating}</span>
                        {skill.totalRatings && (
                          <span>({skill.totalRatings})</span>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 items-center justify-end" onClick={(e) => e.stopPropagation()}>
                      {skill.isInstalled && (
                        <>
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            <CheckCircle className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                            {t('skills.installedBadge')}
                          </Badge>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => navigate(`/personal/skills/${skill.skillPackageId}`)}
                            title={t('skills.viewDetails')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      
                      {!skill.isInstalled && (
                        <Button 
                          variant="default"
                          size="icon"
                          onClick={async () => {
                            try {
                              await apiClient.post('/skills/install', {
                                skillPackageId: skill.skillPackageId,
                                twinId: 'default-twin',
                                enabled: true,
                                approvalMode: 'assisted',
                                allowedDataSources: {
                                  vault: true,
                                  textTraining: true
                                }
                              });
                              toast.success(t('skills.skillInstalled'));
                              await fetchAllData();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || t('errors.operationFailed'));
                            }
                          }}
                          title={t('skills.install')}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {skill.isCustom && (
                        <Button 
                          variant="outline"
                          size="icon"
                          onClick={() => navigate(`/personal/twin/custom-skills/edit/${skill.skillPackageId}`)}
                          title={t('common.edit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          // Check if skill is installed
                          if (skill.isInstalled) {
                            // Show popup telling user to uninstall first
                            toast.error(t('skills.mustUninstallFirst'), {
                              description: t('skills.mustUninstallFirstDesc'),
                              duration: 4000
                            });
                            return;
                          }
                          
                          // Skill is not installed, show confirmation dialog
                          setConfirmDialog({
                            open: true,
                            title: skill.isCustom ? t('skills.confirmDeleteCustom') : t('skills.confirmRemoveFromLibrary'),
                            description: skill.isCustom 
                              ? `${t('skills.confirmDeleteCustomDesc')} "${skill.name}"? ${t('skills.actionPermanent')}`
                              : `${t('skills.confirmRemoveDesc')} "${skill.name}"?`,
                            confirmText: skill.isCustom ? t('common.delete') : t('common.remove'),
                            variant: 'destructive',
                            onConfirm: async () => {
                              try {
                                if (skill.isCustom) {
                                  // Delete custom skill
                                  await apiClient.delete(`/skills/custom/${skill.skillPackageId}`);
                                  toast.success(t('skills.customDeleted'));
                                } else {
                                  // Remove entitlement
                                  try {
                                    await apiClient.delete(`/skills/entitlement/${skill.skillPackageId}`);
                                    toast.success(t('skills.removedFromLibrary'));
                                  } catch (entitlementError) {
                                    // If entitlement doesn't exist (404), the skill might not be properly acquired
                                    // Just refresh the data to sync the UI with actual state
                                    if (entitlementError.response?.status === 404) {
                                      toast.info('This skill was not found in your library. Refreshing...');
                                    } else {
                                      throw entitlementError;
                                    }
                                  }
                                }
                                await fetchAllData();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || t('errors.operationFailed'));
                              }
                            }
                          });
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title={t('skills.remove')}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Empty State */}
            {getFilteredLibrarySkills().length === 0 && (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">{t('skills.noLibrarySkills')}</p>
                <p className="text-sm text-gray-600 mb-4">{t('skills.noLibrarySkillsDesc')}</p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={() => navigate('/personal/marketplace')}>
                    {t('skills.browseMarketplace')}
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/personal/create-skill')}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t('skills.createCustom')}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </Card>
      
      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({...confirmDialog, open})}>
        <AlertDialogContent className="bg-white/95 backdrop-blur-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmDialog({...confirmDialog, open: false})}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDialog.onConfirm) {
                  confirmDialog.onConfirm();
                }
                setConfirmDialog({...confirmDialog, open: false});
              }}
              className={confirmDialog.variant === 'destructive' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {confirmDialog.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
